import React, { useState, useRef } from "react";
import { useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../navbar/Navbar";
import { Icon } from "@iconify/react";
import Footer from "../Footer/Footer";

export default function ViewProduct() {
	const location = useLocation();
	const product = location.state?.product;
	const [selectedImage, setSelectedImage] = useState(0);
	const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
	const [isZoomActive, setIsZoomActive] = useState(false);
	const imageRef = useRef(null);

	useLayoutEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const handleMouseMove = (e) => {
		if (!imageRef.current) return;

		const rect = imageRef.current.getBoundingClientRect();
		const x = (e.nativeEvent.offsetX / rect.width) * 100;
		const y = (e.nativeEvent.offsetY / rect.height) * 100;

		setZoomPosition({ x, y });
	};

	const handleMouseEnter = () => {
		setIsZoomActive(true);
	};

	const handleMouseLeave = () => {
		setIsZoomActive(false);
	};

	if (!product) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<h1 className="text-2xl font-light text-gray-800 mb-4">
						Product not found
					</h1>
					<Link
						to="/prom/2025"
						className="text-[#8a459f] hover:text-[#6b2f80] transition-colors duration-300">
						Return to Collection
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-white">
			<Navbar />
			{/* Header with Back Button */}
			<header className="border-b border-gray-100 mt-[100px]">
				<div className="w-full px-6 sm:px-8 lg:px-12 py-6 bg-pink-400 gap-2  flex justify-center items-center">
					<div className="inline-flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors duration-300 group">
						<span className="font-light tracking-wide text-white">
							{product.type?.toUpperCase()} {product.year}
						</span>
					</div>
					<div className="">
						<Icon
							icon="weui:arrow-outlined"
							width="20"
							height="20"
							color="pink"
						/>
					</div>
					<div className="inline-flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors duration-300 group">
						<span className="font-light tracking-wide text-white">
							{product.style}
						</span>
					</div>
				</div>
			</header>

			{/* Main Product Section */}
			<main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
					{/* Image Gallery */}
					<div className="space-y-6">
						{/* Main Image with CSS-based Zoom Effect */}
						<div
							className="relative overflow-hidden rounded-2xl bg-gray-50"
							style={{
								height: "600px",
								"--zoom-x": `${zoomPosition.x}%`,
								"--zoom-y": `${zoomPosition.y}%`,
								"--display": isZoomActive ? "block" : "none",
							}}>
							<div
								ref={imageRef}
								className="w-full h-full cursor-crosshair"
								onMouseMove={handleMouseMove}
								onMouseEnter={handleMouseEnter}
								onMouseLeave={handleMouseLeave}
								style={{
									position: "relative",
								}}>
								{/* Main Image */}
								<img
									src={
										product.images[selectedImage].startsWith("http")
											? product.images[selectedImage]
											: `https://admin.monsinidress.com/${product.images[selectedImage]}`
									}
									alt={product.style}
									className="w-full h-full object-cover lg:object-[50%_20%] object-top"
								/>

								{/* Zoom Overlay */}
								<div
									className="absolute inset-0 transition-opacity duration-300"
									style={{
										opacity: isZoomActive ? 1 : 0,
										backgroundImage: `url(https://admin.monsinidress.com/${product.images[selectedImage]})`,
										backgroundSize: "200%",
										backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
										pointerEvents: "none",
									}}
								/>
							</div>

							{/* Zoom Indicator */}
							<div
								className={`absolute bottom-4 right-4 bg-pink-500 text-white w-[50px] flex  items-center justify-center  px-0 py-[14px] rounded-full text-sm transition-all duration-300 ${
									isZoomActive ? "opacity-100" : "opacity-0"
								}`}>
								<Icon icon="iconamoon:zoom-in-light" width="20" height="20" />
							</div>
						</div>

						{/* Thumbnail Grid */}
						{product.images.length > 1 && (
							<div className="grid grid-cols-4 gap-4">
								{product.images.map((img, i) => (
									<div
										key={i}
										className={`relative aspect-square overflow-hidden rounded-xl bg-gray-50 cursor-pointer group transition-all duration-300 ${
											selectedImage === i
												? "ring-2 ring-pink-600"
												: "ring-1 ring-pink-200"
										}`}
										onClick={() => {
											setSelectedImage(i);
											setIsZoomActive(false);
										}}>
										<img
											src={
												img.startsWith("http")
													? img
													: `https://admin.monsinidress.com/${img}`
											}
											alt={`${product.style} view ${i + 1}`}
											className="w-full h-full object-cover object-[50%_20%] transition-transform duration-300 group-hover:scale-110"
										/>
										<div
											className={`absolute inset-0 transition-colors duration-300 rounded-xl ${
												selectedImage === i
													? "border-2 border-pink-600"
													: "border-2 border-transparent group-hover:border-gray-300"
											}`}
										/>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Product Information */}
					<div className="flex flex-col">
						{/* Product Header */}
						<div className="mb-8">
							<h1 className="text-3xl font-light text-gray-900 tracking-tight mb-3">
								{product.style}
							</h1>
						</div>

						{/* Divider */}
						<div className="w-16 h-px bg-pink-300 mb-8" />

						{/* Product Details */}
						<div className="space-y-6 mb-8">
							<div>
								<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
									Collection Year
								</h3>
								<p className="text-lg font-light text-gray-800">
									{product.year}
								</p>
							</div>

							<div>
								<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
									Available Sizes
								</h3>
								<p className="text-lg font-light text-gray-800">
									{product.size}
								</p>
							</div>
							<div>
								<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
									Availability
								</h3>
								<p className="text-lg font-light text-gray-800 capitalize">
									{product.availability}
								</p>
							</div>

							{/* Colors */}
							<div>
								<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
									Color Variations
								</h3>
								<div className="flex flex-wrap gap-3">
									{product.colors.map((color, index) => (
										<span
											key={index}
											className="px-4 py-2 bg-white border border-pink-200 rounded-full text-sm font-light text-pink-700 hover:border-pink-400 transition-colors duration-200 cursor-default">
											{color}
										</span>
									))}
								</div>
								<div className="mt-[20px]">
									<Link className="h-fit " to={"/contact"}>
										<button className="w-full border border-pink-500 text-pink-500 py-4 px-8 rounded-full hover:bg-pink-50 transition-all duration-300 font-light tracking-wide text-sm uppercase">
											Inquire About This Gown
										</button>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
}
